import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  InputAdornment,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Swal from "sweetalert2";
import { customerservice, setupManagementUrl, SDLSUrl } from "../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import axios from "axios";

const SectionHeader = ({ title }) => (
  <>
    <Typography
      variant="overline"
      sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1 }}
    >
      {title}
    </Typography>
    <Divider sx={{ mb: 1.5, mt: 0.25 }} />
  </>
);

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500}>
      {value ?? "N/A"}
    </Typography>
  </Box>
);

const BookDeviceLoan = () => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [amount, setAmount] = useState("");
  const [loanProductSelected, setLoanProductSelected] = useState([]);
  const [loanProduct, setLoanProduct] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [installmentError, setInstallmentError] = useState("");
  const [noOfInstallments, setNoOfInstallments] = useState(0);
  const [customerDevices, setCustomerDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const base_url = customerservice.uri;

  const resetForm = () => {
    setSearchTerm("");
    setFilteredCustomer(null);
    setAmount("");
    setLoanProductSelected("");
    setNoOfInstallments("");
    setInstallmentError("");
    setCustomerDevices([]);
    setSelectedDeviceId(null);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 10) {
        const variations = [
          searchTerm,
          searchTerm.startsWith("0") ? searchTerm.replace(/^0/, "254") : null,
          searchTerm.startsWith("254") ? searchTerm.replace(/^254/, "0") : null,
          searchTerm.startsWith("01") ? searchTerm.replace(/^01/, "254") : null, // Handle numbers starting with 01
        ].filter(Boolean);

        setLoadingSearch(true);

        try {
          let foundCustomer = null;

          for (const number of variations) {
            const query = `
              query {
                customersByColumnValues(customer: {mobileNumber: "${number}"}) {
                  error
                  results
                }
              }
            `;

            const token = localStorage.getItem("token");
            const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

            const response = await axios.post(
              base_url,
              { query },
              {
                headers: {
                  Authorization: formattedToken,
                  "Content-Type": "application/json",
                },
              },
            );

            const data = response.data?.data?.customersByColumnValues;

            if (data?.results) {
              const parsedResults = JSON.parse(data?.results);
              if (parsedResults.length) {
                foundCustomer = parsedResults[0];
                break; // Exit the loop if customer is found
              }
            } else if (data?.error) {
              Swal.fire("Error", data.error || "Unexpected error", "error");
              break; // Exit the loop on server-side error
            }
          }

          if (foundCustomer) {
            setFilteredCustomer(foundCustomer);
          } else {
            Swal.fire(
              "Customer Not Found",
              "Customer does not exist with any variations.",
              "warning",
            );
          }
          const params = {
            companyId: parseInt(companyId),
            isDeviceCustomer: true,
          };
          const productResponse = await fetchJsonDynamic(
            "individualProducts",
            "parameters",
            params,
            setupManagementUrl.uri,
          );

          const allProduct = JSON.parse(productResponse.results) || [];

          setLoanProduct(allProduct || []);

          if (!foundCustomer) {
            Swal.fire(
              "Customer Not Found",
              "Customer does not exist with any variations.",
              "warning",
            );
          }
        } catch (error) {
          Swal.fire(
            "Error",
            "An error occurred while searching. Please try again.",
            "error",
          );
          console.error("Error fetching customer by mobile number:", error);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setFilteredCustomer(null);
        setLoadingSearch(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    const fetchCustomerDevices = async () => {
      setSelectedDeviceId(null);
      if (!filteredCustomer?.Id) {
        setCustomerDevices([]);
        return;
      }
      setLoadingDevices(true);
      try {
        const response = await fetchJsonDynamic(
          "deviceRegistry",
          "param",
          {
            customerId: parseInt(filteredCustomer.Id),
            maziwaStatus: "PENDING_LOAN_MAPPING",
          },
          SDLSUrl.uri,
        );
        const devices = response?.results ? JSON.parse(response.results) : [];
        // Only devices already linked to this customer but not yet
        // attached to a loan are eligible to back a new device loan.
        const eligible = (Array.isArray(devices) ? devices : []).filter(
          (device) => !device.LoanId,
        );
        setCustomerDevices(eligible);
      } catch (error) {
        console.error("Error fetching customer devices:", error);
        setCustomerDevices([]);
      } finally {
        setLoadingDevices(false);
      }
    };
    fetchCustomerDevices();
  }, [filteredCustomer]);

  const handleDeviceSelect = (deviceId) => {
    setSelectedDeviceId(deviceId);
  };

  const handleAppraisalSubmit = async () => {
    // Validate installments if dynamic installment is enabled
    if (selectedLoanProduct.IsdynamicInstallment === 1) {
      const min = selectedLoanProduct.MinimumTerm || 0;
      const max = selectedLoanProduct.MaximumTerm || Infinity;
      const numInstallments = parseInt(noOfInstallments, 10);

      if (!noOfInstallments || isNaN(numInstallments)) {
        setInstallmentError("Please enter a valid number of installments");
        return;
      }

      if (numInstallments < min || numInstallments > max) {
        setInstallmentError(`Installments must be between ${min} and ${max}`);
        return;
      }
    }

    if (!selectedDeviceId) {
      Swal.fire({
        icon: "warning",
        title: "Device Required",
        text: "Please select the device to back this loan.",
      });
      return;
    }

    setLoadingSubmit(true);

    try {
      const mutationData = {
        p_CustomerId: filteredCustomer.Id,
        p_Principle: parseInt(amount),
        p_ProductId: loanProductSelected,
        p_DeviceId: selectedDeviceId,
        p_Installment: parseInt(noOfInstallments, 10) || null,
      };

      const response = await executeAnyMutation(
        "insertLoanApplications",
        mutationData,
        "loanApplication",
        false,
        SDLSUrl.uri,
      );
      const parsedResults = JSON.parse(response.results);
      if (parsedResults && parsedResults.length) {
        if (parsedResults[0] === "Loan Booked Successfully") {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: parsedResults[0],
          });
          resetForm();
        } else {
          const escapeHtml = (value) =>
            String(value)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
          Swal.fire({
            icon: "error",
            title: parsedResults.length > 1 ? "Loan Declined" : "Error!",
            html: `<ul style="text-align:left; margin:0; padding-left:1.2em;">${parsedResults
              .map((reason) => `<li>${escapeHtml(reason)}</li>`)
              .join("")}</ul>`,
          });
        }
      } else if (response.errors) {
        throw new Error(
          response.errors[0].Error || "An internal server error occurred.",
        );
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
      resetForm();
    } finally {
      setLoadingSubmit(false);
    }
  };

  const date = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const selectedLoanProduct =
    loanProduct.find((product) => product.Id === loanProductSelected) || {};

  const getDeviceLabel = (device) =>
    `${device.ModelName || "Device"} — IMEI ${device.Imei1}`;

  return (
    <Box sx={{ padding: 3, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700}>
        Book A Device Loan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Search a customer by phone number to begin a new device loan
        application.
      </Typography>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 2 }}>
        <TextField
          label="Search Customer by Phone Number"
          placeholder="e.g. 0712345678"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loadingSearch && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {filteredCustomer && (
        <>
          <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="Customer Details" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    label="Customer Name"
                    value={`${filteredCustomer.FirstName || ""} ${filteredCustomer.OtherNames || ""}`}
                  />
                  <Field label="Email" value={filteredCustomer.EmailAddress} />
                  <Field
                    label="ID Number"
                    value={filteredCustomer.IdNoOrPassport}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    label="Mobile Number"
                    value={filteredCustomer.MobileNumber}
                  />
                  <Field label="Branch" value={filteredCustomer.BranchName} />
                  <Field
                    label="Date Created"
                    value={
                      filteredCustomer.DateCreated
                        ? formatter.format(
                            new Date(filteredCustomer.DateCreated),
                          )
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="Loan Details" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Loan Product</InputLabel>
                    <Select
                      value={loanProductSelected}
                      onChange={(e) => setLoanProductSelected(e.target.value)}
                      defaultValue=""
                      label="Loan Product"
                    >
                      <MenuItem value="" disabled>
                        Select Loan Product
                      </MenuItem>
                      {loanProduct.map((company) => (
                        <MenuItem key={company.Id || 0} value={company.Id || 0}>
                          {company.Name || ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Amount to Disburse"
                    placeholder="Enter Amount"
                    type="text"
                    inputMode="numeric"
                    value={amount === "" ? "" : Number(amount).toLocaleString()}
                    onChange={(e) => {
                      const raw = e.target.value
                        .replace(/,/g, "")
                        .replace(/[^0-9]/g, "");
                      setAmount(raw);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">KES</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {selectedLoanProduct.IsdynamicInstallment === 1 && (
                <Box
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    p: 1.5,
                    mt: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Minimum Installment"
                        value={selectedLoanProduct.MinimumTerm || ""}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Maximum Installment"
                        value={selectedLoanProduct.MaximumTerm || ""}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="No of Installments"
                        placeholder="Enter Number of Installments"
                        value={noOfInstallments}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*$/.test(value)) {
                            setNoOfInstallments(value);
                            setInstallmentError("");
                          }
                        }}
                        error={!!installmentError}
                        helperText={
                          installmentError ||
                          `Enter between ${selectedLoanProduct.MinimumTerm} and ${selectedLoanProduct.MaximumTerm}`
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="Device" />

              {loadingDevices ? (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Checking eligible devices...
                  </Typography>
                </Box>
              ) : customerDevices.length === 0 ? (
                <Alert severity="info">
                  No devices linked to this customer are available for a new
                  loan.
                </Alert>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 2, maxHeight: 320, overflowY: "auto" }}
                >
                  <List dense disablePadding>
                    {customerDevices.map((device, index) => {
                      const checked = selectedDeviceId === device.Id;
                      return (
                        <React.Fragment key={device.Id || device.Imei1}>
                          {index > 0 && <Divider component="li" />}
                          <ListItem
                            disablePadding
                            secondaryAction={
                              <Chip
                                size="small"
                                label={device.MaziwaStatus || "N/A"}
                                color={checked ? "primary" : "default"}
                                variant={checked ? "filled" : "outlined"}
                                sx={{ mr: 1, fontWeight: 600 }}
                              />
                            }
                          >
                            <ListItemButton
                              onClick={() => handleDeviceSelect(device.Id)}
                              dense
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Radio
                                  edge="start"
                                  checked={checked}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText primary={getDeviceLabel(device)} />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </CardContent>
          </Card>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ mt: 1, borderRadius: 2, py: 1.5 }}
            onClick={handleAppraisalSubmit}
            disabled={
              loadingSubmit ||
              !selectedDeviceId ||
              (selectedLoanProduct.IsdynamicInstallment === 1 &&
                (!noOfInstallments ||
                  noOfInstallments < selectedLoanProduct.MinInstallments ||
                  noOfInstallments > selectedLoanProduct.MaxInstallments))
            }
          >
            {loadingSubmit ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit For Appraisal"
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

export default BookDeviceLoan;
